/**
 * /progress-billings/:pbId — AIA G702/G703 Progress Billing detail page.
 *
 * Header: application_no, period, status (G702 summary area).
 * G703 Schedule of Values table: all line items.
 * G702 summary: totals, retention, current due.
 *
 * Loads through the direct billing endpoint; never scans the project list.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useConstructionPack } from "@/lib/useConstructionPack";
import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DataGridPro, type GridColDef } from "@mui/x-data-grid-pro";
import { api, downloadFile, errorMessage } from "@/lib/api";
import { useToast } from "@/components/Toaster";
import { gridDefaults } from "@/grids/gridDefaults";
import type {
  ARInvoice,
  ProgressBilling,
  ProgressBillingEvent,
  ProgressBillingReconciliation,
  ProgressBillingReverseResult,
  ProgressBillingStatus,
  ProgressBillingValidation,
  StoredMaterialMovement,
} from "@/lib/types";

export const Route = createFileRoute("/_auth/progress-billings/$pbId")({
  component: PBDetailPage,
});

// ── Status colours ────────────────────────────────────────────

const PB_STATUS_COLORS: Record<ProgressBillingStatus, { bg: string; fg: string }> = {
  draft:     { bg: "#e2e8f0", fg: "#475569" },
  submitted: { bg: "#dbeafe", fg: "#1e40af" },
  approved:  { bg: "#d1fae5", fg: "#065f46" },
  invoiced:  { bg: "#dcfce7", fg: "#166534" },
  paid:      { bg: "#f0fdf4", fg: "#15803d" },
  rejected:  { bg: "#fee2e2", fg: "#991b1b" },
  reversed:  { bg: "#f1f5f9", fg: "#64748b" },
};

function fmt(val: string | number | null | undefined): string {
  if (val === null || val === undefined || val === "") return "—";
  return Number(val).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

function fmtPct(val: string | number | null | undefined): string {
  if (val === null || val === undefined || val === "") return "—";
  return `${Number(val).toFixed(1)}%`;
}

// ── PB resolution hook ────────────────────────────────────────
// Scans cached progress-billing queries to find the one matching pbId.
// Falls back to fetching per-project if needed.

function usePBDetail(pbId: string) {
  const query = useQuery({
    queryKey: ["progress-billing", pbId],
    queryFn: () => api<ProgressBilling>(`/api/projects/progress-billings/${pbId}`),
    enabled: Boolean(pbId),
  });
  return { pb: query.data ?? null, loading: query.isLoading };
}

type G703GridRow = ProgressBilling["lines"][number] & {
  totalCompleted: number;
  pctComplete: number;
  retention: number;
  balanceToFinish: number;
  workRetentionPercent: number;
  materialRetentionPercent: number;
};

// ── Page component ────────────────────────────────────────────

function PBDetailPage() {
  const { enabled: constructionEnabled } = useConstructionPack();
  if (!constructionEnabled) return null;

  const { pbId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [action, setAction] = useState<"reject" | "reverse" | null>(null);
  const [reason, setReason] = useState("");
  const [reversalDate, setReversalDate] = useState("");
  const { pb, loading } = usePBDetail(pbId);
  const reconciliationQuery = useQuery({
    queryKey: ["progress-billing", pbId, "reconciliation"],
    queryFn: () => api<ProgressBillingReconciliation>(
      `/api/projects/progress-billings/${pbId}/reconciliation`,
    ),
    enabled: Boolean(pb),
  });
  const movementsQuery = useQuery({
    queryKey: ["progress-billing", pbId, "stored-material-movements"],
    queryFn: () => api<StoredMaterialMovement[]>(
      `/api/projects/${pb!.project_id}/stored-material-movements?limit=100`,
    ),
    enabled: Boolean(pb?.contract_id),
  });
  const validationStage = pb?.status === "submitted" ? "approve" : "submit";
  const validationQuery = useQuery({
    queryKey: ["progress-billing", pbId, "validation", validationStage],
    queryFn: () => api<ProgressBillingValidation>(
      `/api/projects/progress-billings/${pbId}/validation?stage=${validationStage}`,
    ),
    enabled: Boolean(pb) && !["paid", "reversed"].includes(pb!.status),
  });
  const eventsQuery = useQuery({
    queryKey: ["progress-billing", pbId, "events"],
    queryFn: () => api<ProgressBillingEvent[]>(
      `/api/projects/progress-billings/${pbId}/events`,
    ),
    enabled: Boolean(pb),
  });
  const arInvoiceQuery = useQuery({
    queryKey: ["ar-invoices", "detail", pb?.ar_invoice_id],
    queryFn: () => api<ARInvoice>(`/api/ar/invoices/${pb!.ar_invoice_id}`),
    enabled: Boolean(pb?.ar_invoice_id),
  });

  const refresh = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["progress-billing", pbId] });
  }, [pbId, qc]);

  const rejectMut = useMutation({
    mutationFn: () => api<ProgressBilling>(
      `/api/projects/progress-billings/${pbId}/reject`,
      { method: "POST", body: { reason } },
    ),
    onSuccess: () => {
      setAction(null);
      setReason("");
      refresh();
      toast.success("Application rejected with an audit reason");
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  const reopenMut = useMutation({
    mutationFn: () => api<ProgressBilling>(
      `/api/projects/progress-billings/${pbId}/reopen`,
      { method: "POST" },
    ),
    onSuccess: () => {
      refresh();
      toast.success("Application reopened as a draft");
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  const reverseMut = useMutation({
    mutationFn: () => api<ProgressBillingReverseResult>(
      `/api/projects/progress-billings/${pbId}/reverse-and-revise`,
      { method: "POST", body: { reason, reversal_date: reversalDate } },
    ),
    onSuccess: (result) => {
      setAction(null);
      setReason("");
      toast.success(`Application #${result.replacement.application_no} Rev ${result.replacement.revision_no} created`);
      navigate({
        to: "/progress-billings/$pbId",
        params: { pbId: result.replacement.id },
      });
    },
    onError: (err: unknown) => toast.error(errorMessage(err)),
  });

  const handleDownload = useCallback(() => {
    if (!pb) return;
    downloadFile(
      `/api/projects/progress-billings/${pb.id}/pdf`,
      `G702-G703-App${pb.application_no}-${pb.billing_date}.pdf`,
    ).catch((err: Error) => alert(err.message));
  }, [pb]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" pt={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (!pb) {
    return (
      <Box p={4}>
        <Typography color="error">Progress billing not found.</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate({ to: "/progress-billings" })}>
          Back to Progress Billings
        </Button>
      </Box>
    );
  }

  const col = PB_STATUS_COLORS[pb.status] ?? PB_STATUS_COLORS.draft;

  // G703 computed columns
  const lines = pb.lines ?? [];
  const g703: G703GridRow[] = lines.map((l) => {
    const scheduled = Number(l.scheduled_value);
    const prev = Number(l.work_completed_previous);
    const thisPeriod = Number(l.work_completed_this_period);
    const stored = Number(l.materials_stored);
    const totalCompleted = prev + thisPeriod + stored;
    const pctComplete = scheduled > 0 ? (totalCompleted / scheduled) * 100 : 0;
    const workRetentionPercent = Number(
      l.work_retainage_percent ?? l.retention_percent,
    );
    const materialRetentionPercent = Number(
      l.material_retainage_percent ?? l.retention_percent,
    );
    const retention = (
      (prev + thisPeriod) * workRetentionPercent
      + stored * materialRetentionPercent
    ) / 100;
    const balanceToFinish = scheduled - totalCompleted;
    return {
      ...l,
      totalCompleted,
      pctComplete,
      retention,
      balanceToFinish,
      workRetentionPercent,
      materialRetentionPercent,
    };
  });

  // G702 summary totals
  const scheduledValueTotal = g703.reduce((s, l) => s + Number(l.scheduled_value), 0);
  const workCompletedTotal  = g703.reduce((s, l) => s + l.totalCompleted, 0);
  const percentComplete = scheduledValueTotal > 0
    ? (workCompletedTotal / scheduledValueTotal) * 100
    : 0;
  const g703Columns: GridColDef<G703GridRow>[] = [
    { field: "line_no", headerName: "Line", width: 65 },
    { field: "description", headerName: "Description", minWidth: 220, flex: 1 },
    { field: "scheduled_value", headerName: "Scheduled", width: 120, align: "right", headerAlign: "right", renderCell: ({ row }) => fmt(row.scheduled_value) },
    { field: "work_completed_previous", headerName: "Prior work", width: 115, align: "right", headerAlign: "right", renderCell: ({ row }) => fmt(row.work_completed_previous) },
    { field: "work_completed_this_period", headerName: "This period", width: 115, align: "right", headerAlign: "right", renderCell: ({ row }) => fmt(row.work_completed_this_period) },
    { field: "materials_stored_previous", headerName: "Prior stored", width: 115, align: "right", headerAlign: "right", renderCell: ({ row }) => fmt(row.materials_stored_previous) },
    { field: "materials_stored", headerName: "Stored", width: 105, align: "right", headerAlign: "right", renderCell: ({ row }) => fmt(row.materials_stored) },
    { field: "totalCompleted", headerName: "Completed", width: 115, align: "right", headerAlign: "right", renderCell: ({ row }) => fmt(row.totalCompleted) },
    { field: "pctComplete", headerName: "%", width: 75, align: "right", headerAlign: "right", renderCell: ({ row }) => fmtPct(row.pctComplete) },
    { field: "balanceToFinish", headerName: "Remaining", width: 115, align: "right", headerAlign: "right", renderCell: ({ row }) => <span className={row.balanceToFinish < 0 ? "font-semibold text-red-600" : ""}>{fmt(row.balanceToFinish)}</span> },
    { field: "workRetentionPercent", headerName: "Work ret.", width: 90, align: "right", headerAlign: "right", renderCell: ({ row }) => fmtPct(row.workRetentionPercent) },
    { field: "materialRetentionPercent", headerName: "Mat. ret.", width: 90, align: "right", headerAlign: "right", renderCell: ({ row }) => fmtPct(row.materialRetentionPercent) },
    { field: "retention", headerName: "Retention", width: 110, align: "right", headerAlign: "right", renderCell: ({ row }) => fmt(row.retention) },
  ];
  const movementColumns: GridColDef<StoredMaterialMovement>[] = [
    { field: "occurred_on", headerName: "Date", width: 110 },
    { field: "movement_type", headerName: "Movement", width: 120 },
    {
      field: "amount_delta",
      headerName: "Change",
      width: 120,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => (
        <span className={Number(row.amount_delta) < 0 ? "text-amber-700" : "text-emerald-700"}>
          {Number(row.amount_delta) > 0 ? "+" : ""}{fmt(row.amount_delta)}
        </span>
      ),
    },
    {
      field: "balance_after",
      headerName: "Balance after",
      width: 130,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => fmt(row.balance_after),
    },
    { field: "notes", headerName: "Source", minWidth: 180, flex: 1 },
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Page header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <button
            className="text-xs text-blue-600 hover:underline"
            onClick={() => navigate({ to: "/progress-billings" })}
          >
            ← Progress Billings
          </button>
          <span className="font-semibold text-slate-800 dark:text-slate-200">
            Application #{pb.application_no}{pb.revision_no ? ` · Rev ${pb.revision_no}` : ""}
          </span>
          <Chip
            size="small"
            label={pb.status}
            sx={{
              fontSize: 10, height: 20,
              bgcolor: col.bg, color: col.fg, fontWeight: 600,
            }}
          />
          <div className="flex-1" />
          {pb.status === "submitted" && (
            <Button
              size="small"
              color="error"
              variant="outlined"
              onClick={() => { setReason(""); setAction("reject"); }}
            >
              Reject
            </Button>
          )}
          {pb.status === "rejected" && (
            <Button
              size="small"
              variant="outlined"
              disabled={reopenMut.isPending}
              onClick={() => reopenMut.mutate()}
            >
              Reopen draft
            </Button>
          )}
          {pb.status === "invoiced" && (
            <Button
              size="small"
              color="warning"
              variant="outlined"
              disabled={Number(arInvoiceQuery.data?.received_amount ?? 0) > 0}
              onClick={() => {
                setReason("");
                setReversalDate(pb.billing_date);
                setAction("reverse");
              }}
            >
              Reverse &amp; revise
            </Button>
          )}
          <Button size="small" variant="outlined" onClick={handleDownload}>
            Download G702/G703 PDF
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 py-4 space-y-6">
        {/* ── G702 Header (application metadata) ── */}
        <section>
          <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-2 dark:text-slate-400">
            G702 — Application for Payment
          </div>
          <div className="grid grid-cols-3 gap-4 max-w-3xl">
            <div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wide dark:text-slate-400">
                Application no.
              </div>
              <div className="font-mono font-semibold">#{pb.application_no}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wide dark:text-slate-400">
                Period
              </div>
              <div className="text-sm">{pb.period_from} → {pb.period_to}</div>
            </div>
            <div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wide dark:text-slate-400">
                Billing date
              </div>
              <div className="text-sm">{pb.billing_date}</div>
            </div>
          </div>

          {pb.notes && (
            <p className="text-xs text-slate-600 mt-3 italic dark:text-slate-400">
              {pb.notes}
            </p>
          )}
          {pb.supersedes_progress_billing_id && (
            <button
              className="mt-3 text-xs text-blue-600 hover:underline"
              onClick={() => navigate({
                to: "/progress-billings/$pbId",
                params: { pbId: pb.supersedes_progress_billing_id! },
              })}
            >
              View the application this revision replaces
            </button>
          )}
          {pb.status === "reversed" && pb.reversal_reason && (
            <Alert severity="info" sx={{ mt: 2, maxWidth: 900 }}>
              Reversed: {pb.reversal_reason}
            </Alert>
          )}
        </section>

        {arInvoiceQuery.data && (
          <section className="max-w-3xl rounded border border-slate-200 p-3 dark:border-slate-700">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Linked accounts receivable
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Invoice</div>
                <div className="text-sm font-semibold">{arInvoiceQuery.data.invoice_no}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500">AR status</div>
                <div className="text-sm capitalize">{arInvoiceQuery.data.status.replaceAll("_", " ")}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Received</div>
                <div className="text-sm">{arInvoiceQuery.data.currency_code} {fmt(arInvoiceQuery.data.received_amount)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500">Open balance</div>
                <div className="text-sm font-semibold">
                  {arInvoiceQuery.data.currency_code} {fmt(
                    Number(arInvoiceQuery.data.total) - Number(arInvoiceQuery.data.received_amount),
                  )}
                </div>
              </div>
            </div>
            {Number(arInvoiceQuery.data.received_amount) > 0 && pb.status === "invoiced" && (
              <Alert severity="info" sx={{ mt: 2 }}>
                This application has customer receipts. Use an AR credit workflow before replacing it.
              </Alert>
            )}
          </section>
        )}

        {validationQuery.data && validationQuery.data.blockers.length > 0 && (
          <section>
            <Alert severity="error">
              <strong>This application cannot {validationStage === "approve" ? "be approved" : "be submitted"} yet.</strong>
              <ul className="list-disc pl-5 mt-1">
                {validationQuery.data.blockers.map((blocker) => (
                  <li key={`${blocker.code}-${blocker.message}`}>{blocker.message}</li>
                ))}
              </ul>
            </Alert>
          </section>
        )}

        {reconciliationQuery.data && (
          <section>
            <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-2 dark:text-slate-400">
              Contract reconciliation
            </div>
            {!reconciliationQuery.data.canonical ? (
              <Alert severity="info" sx={{ mb: 1 }}>
                This is a legacy application and is not yet reconciled to the canonical
                owner SOV. Use the adoption workflow before relying on revised-contract
                comparisons.
              </Alert>
            ) : reconciliationQuery.data.findings.length > 0 ? (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {reconciliationQuery.data.findings.join(" ")}
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mb: 1 }}>
                This application reconciles to the revised owner SOV with no detected overbilling.
              </Alert>
            )}
            <div className="grid grid-cols-4 gap-3 max-w-4xl">
              {[
                ["Revised contract", reconciliationQuery.data.revised_contract_value],
                ["Completed & stored", reconciliationQuery.data.completed_and_stored],
                ["Remaining", reconciliationQuery.data.remaining_to_bill],
                ["Overbilling", reconciliationQuery.data.overbilling_amount],
              ].map(([label, value]) => (
                <div key={label} className="rounded border border-slate-200 px-3 py-2 dark:border-slate-700">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
                  <div className={`font-mono text-sm font-semibold ${label === "Overbilling" && Number(value) > 0 ? "text-red-600" : ""}`}>
                    {fmt(value)}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── G703 Schedule of Values ── */}
        <section>
          <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-2 dark:text-slate-400">
            G703 — Schedule of Values
          </div>
          {g703.length > 0 && (
            <>
              <Box sx={{ height: 560, width: "100%" }}>
                <DataGridPro<G703GridRow>
                  {...gridDefaults}
                  rows={g703}
                  columns={g703Columns}
                  density="compact"
                  disableRowSelectionOnClick
                  initialState={{
                    pagination: { paginationModel: { pageSize: 100, page: 0 } },
                  }}
                  pageSizeOptions={[50, 100, 250]}
                  sx={{
                    borderColor: "divider",
                    "& .MuiDataGrid-cell": { fontSize: 11 },
                    "& .MuiDataGrid-columnHeaderTitle": { fontSize: 11, fontWeight: 700 },
                  }}
                />
              </Box>
              <div className="grid grid-cols-4 gap-3 mt-3 max-w-4xl">
                {[
                  ["Revised SOV", scheduledValueTotal],
                  ["Completed & stored", workCompletedTotal],
                  ["Percent complete", `${percentComplete.toFixed(1)}%`],
                  ["Remaining", g703.reduce((sum, line) => sum + line.balanceToFinish, 0)],
                ].map(([label, value]) => (
                  <div key={label as string} className="rounded border border-slate-200 px-3 py-2 dark:border-slate-700">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
                    <div className="font-mono text-sm font-semibold">
                      {typeof value === "string" ? value : fmt(value)}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {g703.length === 0 && (
            <p className="text-xs text-slate-400 italic mt-2">No schedule-of-values lines.</p>
          )}
        </section>

        {/* ── G702 Summary ── */}
        <section className="max-w-sm">
          <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-2 dark:text-slate-400">
            G702 — Summary
          </div>
          <Table size="small">
            <TableBody>
              {[
                ["Total completed & stored to date", Number(pb.total_completed_to_date) + Number(pb.total_stored_materials)],
                ["Less: retention", `-${fmt(pb.less_retention)}`],
                ["Less: previous billed", `-${fmt(pb.less_previous_billed)}`],
                ["Current payment due", pb.current_due],
              ].map(([label, value]) => (
                <TableRow key={label as string}>
                  <TableCell
                    sx={{
                      fontSize: 12,
                      color: "text.secondary",
                      borderBottom: "none",
                    }}
                  >
                    {label}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: 13,
                      fontWeight:
                        label === "Current payment due" ? 700 : 400,
                      color:
                        label === "Current payment due"
                          ? "primary.main"
                          : "text.primary",
                      borderBottom: "none",
                    }}
                  >
                    {typeof value === "string" && value.startsWith("-")
                      ? value
                      : fmt(value as string | number)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>

        {pb.contract_id && (
          <section>
            <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-2 dark:text-slate-400">
              Stored-material movement ledger
            </div>
            <p className="text-xs text-slate-500 mb-2 dark:text-slate-400">
              Recent immutable balance changes for this project. Receipts increase
              the stored balance; installation and returns reduce it.
            </p>
            <Box sx={{ height: 330, width: "100%" }}>
              <DataGridPro<StoredMaterialMovement>
                {...gridDefaults}
                rows={movementsQuery.data ?? []}
                columns={movementColumns}
                loading={movementsQuery.isLoading}
                density="compact"
                disableRowSelectionOnClick
                initialState={{
                  pagination: { paginationModel: { pageSize: 25, page: 0 } },
                }}
                pageSizeOptions={[25, 50, 100]}
                localeText={{ noRowsLabel: "No stored-material movements recorded" }}
                sx={{
                  borderColor: "divider",
                  "& .MuiDataGrid-cell": { fontSize: 11 },
                  "& .MuiDataGrid-columnHeaderTitle": { fontSize: 11, fontWeight: 700 },
                }}
              />
            </Box>
          </section>
        )}

        <section className="max-w-4xl">
          <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-2 dark:text-slate-400">
            Lifecycle history
          </div>
          <div className="divide-y divide-slate-200 rounded border border-slate-200 dark:divide-slate-700 dark:border-slate-700">
            {(eventsQuery.data ?? []).map((event) => (
              <div key={event.id} className="flex gap-3 px-3 py-2 text-xs">
                <time className="w-40 shrink-0 text-slate-500">
                  {new Date(event.occurred_at).toLocaleString()}
                </time>
                <div>
                  <span className="font-semibold capitalize">
                    {event.event_type.replaceAll("_", " ")}
                  </span>
                  {event.reason && <span className="text-slate-600"> — {event.reason}</span>}
                  {event.related_progress_billing_id && (
                    <button
                      className="ml-2 text-blue-600 hover:underline"
                      onClick={() => navigate({
                        to: "/progress-billings/$pbId",
                        params: { pbId: event.related_progress_billing_id! },
                      })}
                    >
                      Related revision
                    </button>
                  )}
                </div>
              </div>
            ))}
            {!eventsQuery.isLoading && (eventsQuery.data?.length ?? 0) === 0 && (
              <div className="px-3 py-3 text-xs text-slate-500">
                No lifecycle events are available for this legacy application.
              </div>
            )}
          </div>
        </section>
      </div>

      <Dialog open={action !== null} onClose={() => setAction(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {action === "reject" ? "Reject application" : "Reverse and create revision"}
        </DialogTitle>
        <DialogContent>
          {action === "reverse" && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This creates an immutable AR reversal and a new editable revision. Applications with customer receipts cannot be reversed automatically.
            </Alert>
          )}
          {action === "reverse" && (
            <TextField
              fullWidth
              type="date"
              label="Reversal date"
              value={reversalDate}
              onChange={(event) => setReversalDate(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            fullWidth
            multiline
            minRows={3}
            autoFocus
            label={action === "reject" ? "Reason for rejection" : "Reason for revision"}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            helperText="Required and preserved in the audit history."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAction(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={action === "reject" ? "error" : "warning"}
            disabled={
              !reason.trim()
              || (action === "reverse" && !reversalDate)
              || rejectMut.isPending
              || reverseMut.isPending
            }
            onClick={() => action === "reject" ? rejectMut.mutate() : reverseMut.mutate()}
          >
            {action === "reject" ? "Reject application" : "Reverse and create revision"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
